import { useCallback } from 'react';

import { usePrivateWorkspaceAgentSkillsCatalog } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentSkillsCatalogProvider';

export type UsePrivateWorkspaceAgentSkillsCatalogPaginationResult = {
  hasSkillsListNextPage: boolean;
  isSkillsListFetchNextPageError: boolean;
  isSkillsListFetchingNextPage: boolean;
  /** Scroll sentinel and explicit controls — skips while a page fetch is in flight or the last attempt errored. */
  requestSkillsListNextPage: () => void;
  retrySkillsListNextPage: () => void;
};

/**
 * Shared on-demand pagination for workspace-agent skills surfaces (`/` menu, Actions → Saved skills).
 * The catalog provider loads only the first page on panel mount; consumers call `requestSkillsListNextPage`
 * when the user scrolls near the end of a list (or taps retry after an error).
 */
export const usePrivateWorkspaceAgentSkillsCatalogPagination =
  (): UsePrivateWorkspaceAgentSkillsCatalogPaginationResult => {
    const {
      fetchSkillsListNextPage,
      hasSkillsListNextPage,
      isSkillsListFetchNextPageError,
      isSkillsListFetchingNextPage,
    } = usePrivateWorkspaceAgentSkillsCatalog();

    const requestSkillsListNextPage = useCallback(() => {
      if (!hasSkillsListNextPage || isSkillsListFetchingNextPage || isSkillsListFetchNextPageError) {
        return;
      }

      fetchSkillsListNextPage();
    }, [fetchSkillsListNextPage, hasSkillsListNextPage, isSkillsListFetchNextPageError, isSkillsListFetchingNextPage]);

    const retrySkillsListNextPage = useCallback(() => {
      if (!hasSkillsListNextPage || isSkillsListFetchingNextPage) {
        return;
      }

      fetchSkillsListNextPage();
    }, [fetchSkillsListNextPage, hasSkillsListNextPage, isSkillsListFetchingNextPage]);

    return {
      hasSkillsListNextPage,
      isSkillsListFetchNextPageError,
      isSkillsListFetchingNextPage,
      requestSkillsListNextPage,
      retrySkillsListNextPage,
    };
  };
